import { Button, MenuItem } from "@mui/material";

import { BenefitsPolicyVigencyType, IBenefitsPolicy, IBenefitsPolicyRole } from "../../Setup.types";
import { ButtonDelete, Row, SelectForm, Title } from "./BenefitsPolicy.styled";
import { useSetup } from "../../Setup.context";
import { useQuery } from "@tanstack/react-query";
import api from "services/api";
import { TextField } from "../Rules/Rules.styled";
import { MultiSelectWithSelectAll } from "v2/components/ui";

interface IProps {
  benefitsPolicy: IBenefitsPolicy;
  handleChange: (payload: Partial<IBenefitsPolicy>) => void;
}

export function BenefitsPolicyRole({ benefitsPolicy, handleChange }: IProps) {
  const { setup } = useSetup();
  const { benefitsPolicyByRole } = benefitsPolicy;

  const { data, isFetching, isFetched } = useQuery({
    queryKey: ["organization-roles", setup.rules.rulesRegister.buyerFederalTaxId],
    queryFn: () => api.get(`/v2//operation/organization-roles/${setup.rules.rulesRegister.buyerFederalTaxId}`),
    select: (data) => data?.data?.data as string[],
  });

  if (!isFetched && isFetching) return <div>Loading...</div>;

  /** Opções possíveis da coluna de Cargo. */
  const roleOptions = data ?? [];
  /** Opções possíveis da coluna de Plano. */
  const planOptions = setup.rules.rulesElegibility.plans.map(plan => plan.planName).filter(plan => plan !== undefined);

  /** Função responsável especificamente por receber os valores alterados e formatar corretamente para handleChange. */
  function handleUpdateRole(payload: Partial<IBenefitsPolicyRole>, index: number) {
    const updatedPayload = benefitsPolicyByRole.map((b, i) => i === index ? { ...b, ...payload } : b);

    handleChange({ ...benefitsPolicy, benefitsPolicyByRole: updatedPayload });
  }

  /** Função responsável por remover campo da array de regras de roles. */
  function handleRemoveRole(index: number) {
    const updatedPayload = benefitsPolicyByRole.filter((_b, i) => i !== index);

    return handleChange({ ...benefitsPolicy, benefitsPolicyByRole: updatedPayload });
  }

  /** Função responsável por adicionar novo campo vazio à array de roles. */
  function handleAddRole() {
    const newRole = { roleName: undefined, planName: undefined, effectiveDate: undefined };

    return handleChange({ ...benefitsPolicy, benefitsPolicyByRole: [...benefitsPolicyByRole, newRole] });
  }

  /**
   * Função que verifica se um cargo deve ser desabilitado para a row atual.
   * Regras:
   * - Um cargo só pode ser selecionado 1x para effectiveDate = IMEDIATA
   * - Um cargo só pode ser selecionado 1x para effectiveDate = 12_MONTHS
   * - Se effectiveDate não é "12_MONTHS", implica IMEDIATA
   * - O cargo só pode ser selecionado com 12_MONTHS se o plano for o mesmo da seleção IMEDIATA
   */
  function isRoleDisabled(roleName: string, currentIndex: number): boolean {
    const currentRow = benefitsPolicyByRole[currentIndex];
    const currentEffectiveDate = currentRow.effectiveDate;
    const currentPlan = currentRow.planName;

    // Determina o effectiveDate da row atual (se não for 12_MONTHS, é IMEDIATA)
    const isCurrentRowImmediate = currentEffectiveDate !== "12_MONTHS";

    // Verifica se o cargo já foi selecionado em outras rows
    const isAlreadySelected = benefitsPolicyByRole.some((benefit, index) => {
      // Ignora a row atual
      if (index === currentIndex) return false;

      // Verifica se a row contém o cargo
      const hasRole = benefit.roleName?.includes(roleName);
      if (!hasRole) return false;

      // Determina o effectiveDate da row sendo verificada
      const isOtherRowImmediate = benefit.effectiveDate !== "12_MONTHS";

      // Se ambas as rows têm o mesmo effectiveDate, desabilita
      if (isCurrentRowImmediate === isOtherRowImmediate) {
        return true;
      }

      // Se os effectiveDates são diferentes, só permite se o plano for o mesmo
      // Caso contrário, desabilita
      return benefit.planName !== currentPlan;
    });

    return isAlreadySelected;
  }

  function formatMeses(value: string) {
    const parsed = value.replace(/\D/g, '');

    if (Number(parsed) > 36) return "0";

    return parsed;
  }

  /** Função para obter cargos disponíveis (não desabilitados) para um índice específico. */
  function getAvailableRoles(index: number) {
    return roleOptions.filter(option => !isRoleDisabled(option, index));
  }

  return (
    <>
      <Title>Cargos</Title>
      {benefitsPolicyByRole.map((benefit, index) => (
        <Row key={index} style={{ alignItems: "center"}}>
          <MultiSelectWithSelectAll
            label="Cargo"
            size="small"
            value={benefit.roleName ?? []}
            options={roleOptions}
            onChange={(values) => handleUpdateRole({ roleName: values }, index)}
            getOptionDisabled={(option) => isRoleDisabled(option, index)}
            getAvailableOptions={() => getAvailableRoles(index)}
          />
          <SelectForm
            label="Plano inicial"
            value={benefit.planName}
            onChange={(e) => handleUpdateRole({ planName: e.target.value as string }, index)}
          >
            {planOptions.map(option => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </SelectForm>
          <SelectForm
            label="Upgrade"
            value={benefit.obstetrícia === true ? "SIM" : benefit.obstetrícia === false ? "NAO" : undefined}
            onChange={(e) => handleUpdateRole({ obstetrícia: e.target.value === "SIM" ? true : false }, index)}
          >
            <MenuItem value={"SIM"}>Sim</MenuItem>
            <MenuItem value={"NAO"}>Não</MenuItem>
          </SelectForm>
          <SelectForm
            label="Plano com upgrade"
            value={benefit.planName2}
            onChange={(e) => handleUpdateRole({ planName2: e.target.value as string }, index)}
            disabled={benefit.obstetrícia === false}
          >
            {planOptions.map(option => (
              <MenuItem
                key={option}
                value={option}
              >
                {option}
              </MenuItem>
            ))}
          </SelectForm>
          <TextField
            label="Inicio do upgrade (meses)"
            value={benefit.effectiveDate}
            onChange={(e) => handleUpdateRole({ effectiveDate: formatMeses(e.target.value) as BenefitsPolicyVigencyType }, index)}
            disabled={benefit.obstetrícia === false}
          />
          {/* <SelectForm
            label="Inicio do upgrade"
            value={benefit.effectiveDate}
            onChange={(e) => handleUpdateRole({ effectiveDate: e.target.value as BenefitsPolicyVigencyType }, index)}
            disabled={benefit.obstetrícia === false}
          >
            <MenuItem value={"IMEDIATA"}>Imediata</MenuItem>
            <MenuItem value={"12_MONTHS"}>12 Meses</MenuItem>
          </SelectForm> */}
          <ButtonDelete color="error" onClick={() => handleRemoveRole(index)} />
        </Row>
      ))}
      <Button onClick={handleAddRole}>+ Adicionar</Button>
    </>
  );
}
